import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { qk } from '../lib/queryClient'
import {
  armarDirectorio,
  etiquetaExterno,
  mapaDeNombres,
  resolverAutor,
  urlOriginal,
  esImportado,
  textoHistoria,
  type Autor,
  type ConMarca,
} from '../lib/autoria'
import type { HistoriaBasecamp, PersonaVista, TeamMember } from '../lib/types'
import './autoria.css'

// Autoría para los componentes (D-25). La regla vive en lib/autoria.ts; aquí solo
// se conecta con la caché de /api/team, que ya trae el roster (`members`) y el
// directorio histórico (`personas`) en la MISMA respuesta: pintar autores no
// cuesta una petición más. Separado de common.tsx por lo mismo que photos.tsx:
// apiGet arrastra firebase/auth y common.tsx lo importan pruebas que corren en node.

interface RespuestaTeam {
  members: TeamMember[]
  personas?: PersonaVista[]
}

const VACIO: PersonaVista[] = []

/** Roster + personas históricas, de la caché compartida de /api/team. */
export function useEquipoYPersonas(): { team: TeamMember[]; personas: PersonaVista[] } {
  const { data } = useQuery({
    queryKey: qk.team,
    queryFn: () => apiGet<RespuestaTeam>('/api/team'),
  })
  return { team: data?.members ?? [], personas: data?.personas ?? VACIO }
}

/**
 * `(clave, espejo?, legado?) => Autor` con el orden roster → personas → espejo.
 * Para posts, comentarios, notas, recursos y tareas.
 */
export function useAutor(): (clave: string | null | undefined, espejo?: string | null, legado?: boolean) => Autor {
  const { team, personas } = useEquipoYPersonas()
  const dir = useMemo(() => armarDirectorio(team, personas), [team, personas])
  return useCallback((clave, espejo, legado) => resolverAutor(dir, clave, espejo, legado), [dir])
}

/** Mapa clave → nombre con la misma precedencia, para quien ya trabaja con Map (chat). */
export function useNombres(): Map<string, string> {
  const { team, personas } = useEquipoYPersonas()
  return useMemo(() => mapaDeNombres(team, personas), [team, personas])
}

/** "externo · Telcel" junto al nombre de un autor externo. Nada para un interno. */
export function MarcaExterno({ autor }: { autor: Pick<Autor, 'externo' | 'empresa'> }) {
  const texto = etiquetaExterno(autor)
  if (!texto) return null
  return <span className="tag tag-externo">{texto}</span>
}

function FlechaFuera() {
  // Glifo SVG, no "↗" de texto: un carácter se pinta con la fuente del sistema y
  // cambia de peso y de alineación en cada plataforma (regla de IconoCerrar).
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 16 16 8M10 8h6v6" />
    </svg>
  )
}

/**
 * Chip discreto "De Basecamp" con liga al original. `liga={false}` dentro de una
 * fila que ya es un botón: un <a> dentro de un <button> es HTML inválido y el clic
 * abriría las dos cosas. Sin url (el primer lote no la guardó) sale sin liga.
 * Nada si el documento es nativo.
 *
 * La HISTORIA (archivado en Basecamp o de una herramienta apagada) sale en gris y lo
 * dice: "Archivado en Basecamp" / "Herramienta apagada en Basecamp", para que nadie
 * la lea como algo que se canceló o se escondió en camp.
 */
export function ChipBasecamp({
  doc,
  liga = true,
}: {
  doc: (ConMarca & HistoriaBasecamp) | null | undefined
  liga?: boolean
}) {
  if (!esImportado(doc)) return null
  const url = urlOriginal(doc)
  const archivado = doc?.archivadoEnBasecamp === true
  const apagada = doc?.herramientaApagadaEnBasecamp === true
  const texto = archivado ? 'Archivado en Basecamp' : apagada ? 'Herramienta apagada en Basecamp' : 'De Basecamp'
  const porque = archivado
    ? apagada
      ? 'Estaba archivado, en una herramienta apagada de Basecamp'
      : 'Estaba archivado en Basecamp'
    : apagada
      ? 'Venía de una herramienta que estaba apagada en Basecamp'
      : null
  const clase = `chip chip-basecamp${porque ? ' chip-historia' : ''}`
  if (liga && url) {
    return (
      <a
        className={clase}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={porque ? `${porque}. Abrir el original` : 'Abrir el original en Basecamp'}
        onClick={(e) => e.stopPropagation()}
      >
        {texto}
        <FlechaFuera />
      </a>
    )
  }
  return (
    <span className={clase} title={porque ? `Importado de Basecamp. ${porque}` : 'Importado de Basecamp'}>
      {texto}
    </span>
  )
}

/**
 * "Ver 3 archivados en Basecamp" / "Ocultar …": la historia de una lista va después de
 * lo vivo, en gris y CERRADA de entrada, como las listas archivadas del tablero. Nada
 * si no hay historia.
 */
export function BotonHistoria({
  docs,
  abierta,
  onToggle,
}: {
  docs: readonly HistoriaBasecamp[]
  abierta: boolean
  onToggle: () => void
}) {
  if (docs.length === 0) return null
  return (
    <button type="button" className="btn btn-ghost btn-historia" aria-expanded={abierta} onClick={onToggle}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={abierta ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'} />
      </svg>
      {abierta ? 'Ocultar' : 'Ver'} {textoHistoria(docs)}
    </button>
  )
}
